import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Award, Globe, Heart, Users } from 'lucide-react';

const AboutPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const values = [
    {
      icon: Heart,
      title: 'Passion for Excellence',
      description: 'Every detail matters. We are perfectionists in the art of luxury travel.',
    },
    {
      icon: Globe,
      title: 'Global Expertise',
      description: 'Deep local knowledge combined with worldwide luxury partnerships.',
    },
    {
      icon: Users,
      title: 'Personalized Service',
      description: 'Dedicated travel advisors who understand your unique preferences.',
    },
    {
      icon: Award,
      title: 'Uncompromising Quality',
      description: 'Access to the world\'s most exclusive hotels, experiences, and destinations.',
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      data-testid="about-page"
    >
      {/* Hero */}
      <section className="relative h-[60vh] md:h-[70vh] flex items-center justify-center overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1540962351504-03099e0a754b?q=80&w=2532&auto=format&fit=crop"
            alt="Luxury Travel"
            className="hero-image"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
        </div>

        <div className="relative z-10 container-padding text-center max-w-4xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="font-body text-xs uppercase tracking-[0.3em] text-accent mb-6"
          >
            About Sarvatra
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="font-heading text-5xl md:text-7xl lg:text-8xl font-light tracking-tight leading-[0.9] text-primary mb-6"
            data-testid="about-title"
          >
            The Art of
            <br />
            Bespoke Travel
          </motion.h1>
        </div>
      </section>

      {/* Story */}
      <section className="section-padding container-padding" data-testid="about-story-section">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="font-body text-xs uppercase tracking-[0.3em] text-accent mb-6">
              Our Story
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-light text-primary mb-8">
              Sarvatra: Everywhere, Exceptional
            </h2>
            <div className="space-y-6 text-muted-foreground text-base md:text-lg leading-relaxed">
              <p>
                The Sanskrit word "Sarvatra" translates to "everywhere" — a philosophy that embodies
                our commitment to creating extraordinary travel experiences across the globe. Founded
                on the belief that true luxury lies not in ostentation but in authenticity, exclusivity,
                and genuine human connection.
              </p>
              <p>
                We are not merely travel planners; we are curators of life-defining moments. Each
                journey we design is a carefully orchestrated symphony of culture, comfort, and wonder,
                tailored precisely to the unique desires of discerning travelers who seek more than a
                vacation — they seek transformation.
              </p>
              <p>
                From private island escapes in the Maldives to heritage palace stays in Rajasthan, from
                Arctic expeditions to African safaris, our global network of partnerships grants access
                to experiences money cannot typically buy. Yet it is our deep understanding of luxury —
                the kind that whispers rather than shouts — that truly sets us apart.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="section-padding container-padding bg-white/[0.02]" data-testid="about-philosophy-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="font-body text-xs uppercase tracking-[0.3em] text-accent mb-6">
                Our Philosophy
              </p>
              <h2 className="font-heading text-4xl md:text-5xl font-light text-primary mb-8">
                Craftsmanship in Every Detail
              </h2>
              <p className="text-muted-foreground text-base leading-relaxed mb-6">
                Luxury, to us, is the seamless marriage of impeccable service, authentic experiences,
                and complete privacy. It is the feeling of being perfectly understood without having
                to explain yourself.
              </p>
              <p className="text-muted-foreground text-base leading-relaxed">
                Every itinerary is a bespoke creation, shaped by hours of research, insider knowledge,
                and an intimate understanding of what makes a journey not just memorable, but
                transformative.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="aspect-[4/5] overflow-hidden"
            >
              <img
                src="https://images.unsplash.com/photo-1559339352-11d035aa65de?q=80&w=2574&auto=format&fit=crop"
                alt="Fine Dining Experience"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding container-padding" data-testid="about-values-section">
        <div className="max-w-[1920px] mx-auto">
          <div className="text-center mb-16 md:mb-24">
            <p className="font-body text-xs uppercase tracking-[0.3em] text-accent mb-4">
              What Drives Us
            </p>
            <h2 className="font-heading text-4xl md:text-6xl font-normal tracking-tight text-primary/90">
              Our Core Values
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  className="glass-card p-8 text-center"
                  data-testid={`value-card-${index}`}
                >
                  <Icon className="text-accent mx-auto mb-6" size={48} />
                  <h3 className="font-heading text-2xl font-light text-primary mb-4">
                    {value.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Partnerships */}
      <section className="section-padding container-padding bg-white/[0.02]" data-testid="about-partnerships-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="font-body text-xs uppercase tracking-[0.3em] text-accent mb-6">
              Global Network
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-light text-primary mb-8">
              Luxury Partnerships Worldwide
            </h2>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-3xl mx-auto">
              Our relationships with the world's finest hotels, resorts, and experience providers grant
              our clients unparalleled access and privileges. From Aman to Four Seasons, from private
              island owners to royal heritage properties, we open doors that remain closed to others.
            </p>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default AboutPage;